import { Field } from 'formik';
import { COOKIE_AUTH_DETAILS, ROLES, pageUrls } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { hasEnabledFeature, isdvirOnly, objectArrayFromSameField } from 'globalutils/fmUtils';
import 'rc-tooltip/assets/bootstrap.css';
import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;
const CheckBoxToggle = lazy(() => import(`globalcomponents/UI/Checkbox/${layout}`));
const Link = lazy(() => import(`globalcomponents/UI/Link`));

const LeftForm = ({ values, handleEdit, errors, isNotValid, Styled, hasSaveApiStatus }) => {
  const SERVICE_PROVIDER = window.$environment?.SERVICE_PROVIDER;
  const token = getCookie(COOKIE_AUTH_DETAILS);
  const isDvirOnlyFlagEnabled = isdvirOnly();

  const maintPeriod = objectArrayFromSameField([1, 2, 3, 4, 5, 6, 7]);
  const i18n = useTranslation();

  const showError = (field, validationErrors, isNotValid, apiError) => {
    if (validationErrors[field] && !isNotValid) {
      return <Message type="error" message={errors[field]} />;
    }
    if (apiError?.[field]) {
      return <Message type="error" message={apiError[field]} />;
    }
  };

  const dotUpdation = (value) => {
    return value.substring(0, 9);
  };

  const getSubscriptionTier = (subscriptionTier = 'Tenna') => {
    const currentTier = checkServiceProvider('leftform_getSubscriptionTier', SERVICE_PROVIDER, false, { subscriptionTier });
    return currentTier?.length > 0 ? currentTier?.[0]?.label : '';
  };

  return (
    <Styled.leftSection>
      <div className="companyForm firstRow">
        <FormStyle.FieldsWrapper>
          <Label htmlFor="company" variant="body1">
            {i18n.t('common.company')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="company"
              handleOnChange={(event) => handleEdit(event, { field: 'company' })}
              as={Input}
              placeholder={i18n.t('companyData.companyName')}
              value={values['company']}
              type="text"
              className="borderStyle"
              id="company"
              onLoadFocus
              disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
            />
            {showError('company', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="dotNumber" variant="body1">
            {i18n.t('common.quoteId')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="dotNumber"
              id="dotNumber"
              handleOnChange={(event) => handleEdit(event, { field: 'dotNumber' })}
              as={Input}
              placeholder={i18n.t('common.quoteId')}
              value={values['dotNumber']}
              type="text"
              updateValue={(e) => dotUpdation(e)}
              disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
            />
            {showError('dotNumber', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        {layout !== 'Clutch' && (
          <>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="maintenancePeriod" variant="body1">
                {i18n.t('companyData.maintainancePeriodMonth')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="maintenancePeriod"
                  id="maintenancePeriod"
                  onChange={(event) => handleEdit(event, { field: 'maintenancePeriod' })}
                  as={Select}
                  placeholderText={i18n.t('companyData.maintainancePeriod')}
                  value={values['maintenancePeriod']}
                  suggestions={maintPeriod}
                />
                {showError('maintenancePeriod', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="maintenanceMileage" variant="body1">
                {i18n.t('companyData.maintainanceMileage')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="maintenanceMileage"
                  id="maintenanceMileage"
                  handleOnChange={(event) => handleEdit(event, { field: 'maintenanceMileage' })}
                  as={Input}
                  placeholder={i18n.t('companyData.maintainanceMileage')}
                  value={values['maintenanceMileage']}
                  type="text"
                  className="borderStyle"
                />
                {showError('maintenanceMileage', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <FormStyle.TextWrapper className="subscriptionCompany">
                <Link id="subscriptionTier" newPage href={pageUrls.subscriptionLink}>
                  {i18n.t('registration.subscriptionTier')}
                </Link>
              </FormStyle.TextWrapper>
              <FormStyle.CheckBoxWrapper direction="row">
                <Label variant="body1" className="nowrap">
                  {getSubscriptionTier(values.subscriptionTier)}
                </Label>
              </FormStyle.CheckBoxWrapper>
            </FormStyle.FieldsWrapper>
          </>
        )}
      </div>
      {!token?.roles?.includes(ROLES.SERVICE_MANAGER) && (
        <div className="companyForm">
          {checkServiceProvider('leftform_fields', SERVICE_PROVIDER, false) && (
            <>
              <FormStyle.FieldsWrapper className="valueWrapper">
                <Label variant="body1">{i18n.t('companyData.contractExpirationDate')}</Label>
                <FormStyle.TextWrapper className="values">{values.contractExpirationDate}</FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>

              <FormStyle.FieldsWrapper className="valueWrapper">
                <Label variant="body1">{i18n.t('companyData.currentSubscriptionCost')}</Label>
                <FormStyle.TextWrapper className="values">{values.currentSubscriptionCost}</FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>
            </>
          )}

          <FormStyle.FieldsWrapper className="valueWrapper">
            <Label variant="body1">{checkServiceProvider('leftform_labels', SERVICE_PROVIDER, false)}</Label>
            <FormStyle.TextWrapper className="values">{values.initialTrucks}</FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
          <FormStyle.FieldsWrapper className="valueWrapper">
            <Label variant="body1">{i18n.t('companyData.homeTimeZone')}</Label>
            <FormStyle.TextWrapper className="values">{values.timeZone}</FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
          <div className="checkboxesCompany oldviewAlign">
            {token?.roles?.includes('FLMANAGER') && checkServiceProvider('leftform_oldviewAlign', SERVICE_PROVIDER, false) && (
              <FormStyle.FieldsWrapper htmlFor="skipVehicleFilter" className="agreeTerms">
                <FormStyle.TextWrapper>
                  <CheckBoxToggle
                    aria-labelledby="agree"
                    type="NormalGrey"
                    checked={values.skipVehicleFilter}
                    label={i18n.t('companyData.skipcompanyCheckbox')}
                    onClick={(event) =>
                      handleEdit(event, {
                        field: 'skipVehicleFilter',
                      })
                    }
                    name="skipVehicleFilter"
                  />
                </FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>
            )}
            {token?.roles?.includes('FLMANAGER') && hasEnabledFeature('editOilFieldWaitingEnabled') && !isDvirOnlyFlagEnabled && (
              <FormStyle.FieldsWrapper htmlFor="showOw" className="agreeTerms">
                <FormStyle.TextWrapper>
                  <CheckBoxToggle
                    aria-labelledby="agree"
                    type="NormalGrey"
                    checked={values.showOw}
                    label={i18n.t('companyData.enablecompanyCheckbox')}
                    onClick={(event) =>
                      handleEdit(event, {
                        field: 'showOw',
                      })
                    }
                    name="showOw"
                  />
                </FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>
            )}
          </div>
        </div>
      )}
    </Styled.leftSection>
  );
};

export default LeftForm;
